import React, { useCallback, useMemo } from 'react';

import { useAtomValue } from 'jotai';
import { FormattedMessage } from 'react-intl';

import { useColorMode } from '@atlaskit/app-provider/use-color-mode';
import { useSetColorMode } from '@atlaskit/app-provider/use-set-color-mode';
import { cssMap } from '@atlaskit/css';
import { RovoIcon } from '@atlaskit/logo/rovo/icon';
import Lozenge from '@atlaskit/lozenge/new/lozenge';
import { TopNav, TopNavEnd, TopNavStart } from '@atlaskit/navigation-system/layout/top-nav';
import { AppLogo } from '@atlaskit/navigation-system/top-nav-items';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import AccountMenu from '@atlassian/account-menu/nav4/account-menu-nav4';

import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';
import { getConfig } from '../../../config';
import { useAtlassianUser } from '../../../hooks/use-atlassian-user';
import { NavActionsAtom } from '../../state/NavActionsAtom';
import { TwgAppSwitcher } from '../AppSwitcher';
import { ExplorerIcon } from '../ExplorerIcon';
import { messages } from './messages';

const styles = cssMap({
	labelContainer: {
		display: 'flex',
		alignItems: 'center',
	},
});

type NavigationBarProps = {
	colorMode?: 'light' | 'dark' | 'auto';
	onThemeChange?: (theme: 'light' | 'dark' | 'auto') => void;
};

const GRAPH_INFERENCE_PLATFORM_LABEL = 'Graph Inference Platform';

export const NavigationBar = ({
	colorMode: colorModeOverride,
	onThemeChange,
}: NavigationBarProps = {}): React.JSX.Element => {
	const user = useAtlassianUser();
	const navActions = useAtomValue(NavActionsAtom);
	const { fireEvent } = useAnalyticsEvents();

	const appColorMode = useColorMode();
	const setColorMode = useSetColorMode();
	const colorMode = colorModeOverride ?? appColorMode;

	const handleThemeChange = React.useCallback(
		(theme: 'light' | 'dark' | 'auto') => {
			if (onThemeChange) {
				onThemeChange(theme);
				return;
			}
			setColorMode(theme);
		},
		[onThemeChange, setColorMode],
	);

	const handleIconClick = useCallback(() => {
		fireEvent('ui.button.clicked.gcNavIconClick', {});
		window.location.href = '/graph-explorer';
	}, [fireEvent]);

	const appLabel = useMemo(() => {
		const path = typeof window !== 'undefined' ? window.location.pathname : '';
		if (path.startsWith('/labs')) {
			return 'Labs';
		}
		if (path.startsWith('/catalog')) {
			return 'Catalog';
		}
		if (path.startsWith('/assistant')) {
			return 'Assistant';
		}
		if (path.startsWith('/visualiser')) {
			return 'Visualiser';
		}
		if (path.startsWith('/query-builder')) {
			return 'Query Builder';
		}
		if (path.startsWith('/graph-explorer')) {
			return 'Graph Explorer';
		}
		if (path.startsWith('/cypher-explorer')) {
			return 'Cypher Explorer';
		}
		if (path.startsWith('/workstreams') || path.startsWith('/inferred-projects')) {
			return 'Workstreams';
		}
		if (path.startsWith('/graphclaw-hub')) {
			return GRAPH_INFERENCE_PLATFORM_LABEL;
		}
		return 'Teamwork Graph';
	}, []);

	return (
		<TopNav>
			<TopNavStart sideNavToggleButton={null}>
				<Flex direction="row">
					<TwgAppSwitcher />
					<Flex direction="row" gap="space.025">
						<AppLogo
							href={appLabel === 'Graph Explorer' ? '/graph-explorer' : '/'}
							icon={appLabel === 'Graph Explorer' ? ExplorerIcon : RovoIcon}
							name={appLabel}
							label={appLabel === 'Graph Explorer' ? 'Graph Explorer home' : ''}
							onClick={appLabel === 'Graph Explorer' ? handleIconClick : undefined}
						/>
						{appLabel && (
							<Box xcss={styles.labelContainer}>
								{appLabel === GRAPH_INFERENCE_PLATFORM_LABEL && (
									<Lozenge appearance="default" isBold>
										<FormattedMessage {...messages.internalPreviewTag} />
									</Lozenge>
								)}
								{appLabel === 'Graph Explorer' && (
									<Lozenge appearance="default" isBold>
										<FormattedMessage {...messages.atlassianInternalTag} />
									</Lozenge>
								)}
								{(appLabel === 'Labs' ||
									appLabel === 'Query Builder' ||
									appLabel === 'Assistant') && (
									<Lozenge appearance={appLabel === 'Assistant' ? 'neutral' : 'discovery'} isBold>
										<FormattedMessage
											{...(appLabel === 'Assistant'
												? messages.underDevelopmentTag
												: messages.betaTag)}
										/>
									</Lozenge>
								)}
							</Box>
						)}
					</Flex>
				</Flex>
			</TopNavStart>
			<TopNavEnd>
				{navActions}
				<AccountMenu
					name={user.name}
					picture={user.picture}
					email={user.email}
					viewProfileLink={`${getConfig().atlassianHomeUrl}/people/${encodeURIComponent(user.accountId)}`}
					manageProfileLink={`${getConfig().atlassianAccountUrl}/login?application=dtac&continue=${encodeURIComponent(
						`${getConfig().atlassianAccountUrl}/manage-profile`,
					)}&login_hint=${encodeURIComponent(user.email)}&prompt=none`}
					logOutLink={`${getConfig().atlassianAccountUrl}/logout?application=dtac&continue=${encodeURIComponent(
						getConfig().serviceUrl,
					)}`}
					onThemeChange={handleThemeChange}
					currentTheme={colorMode}
				/>
			</TopNavEnd>
		</TopNav>
	);
};
